import type { ResultRow } from './results';

/** The four skills a result form shows, in the order the real one prints them. */
export const SKILLS = ['LISTENING', 'READING', 'WRITING', 'SPEAKING'] as const;
export type Skill = (typeof SKILLS)[number];

export const SKILL_LABEL: Record<Skill, string> = {
  LISTENING: 'Listening',
  READING: 'Reading',
  WRITING: 'Writing',
  SPEAKING: 'Speaking',
};

export interface SkillBand {
  /** The band, or null where there is none to show yet. */
  band: number | null;
  /** Why there is none, for the line under the tile. */
  state: 'marked' | 'awaiting' | 'not-sat' | 'not-offered';
  /** The result to open, where one exists. */
  resultId: string | null;
}

export interface TestResult {
  /** The whole test — "Test 1" — or the paper's own title where it stands alone. */
  name: string;
  /** Most recent first, by the paper sat last. */
  satAt: string;
  skills: Record<Skill, SkillBand>;
  overall: number | null;
  /** True while a paper sat is still waiting for a person to mark it. */
  awaiting: boolean;
}

/**
 * A mean of bands, rounded the way a result form rounds it.
 *
 * To the nearest half band, and a mean landing exactly between two goes up:
 * 6.25 is a 6.5 and 6.75 is a 7. Multiplying by two and rounding half up does
 * both, because a .25 becomes a .5 and a .75 becomes a 1.5 — which rounds up.
 */
export function meanBand(bands: number[]): number | null {
  if (bands.length === 0) return null;
  const mean = bands.reduce((total, band) => total + band, 0) / bands.length;
  return Math.round(mean * 2) / 2;
}

/**
 * A candidate's results, gathered into the tests they belong to.
 *
 * A paper is sat one at a time and marked one at a time, but a candidate wants
 * to read the test: four skills and what they come to. Papers are grouped by
 * the test they were set as part of, and a paper set on its own is a test of
 * one.
 *
 * Speaking is never offered here — this platform sets Listening, Reading and
 * Writing — so it is shown and marked as not offered rather than left out. A
 * candidate comparing this with the real thing should see the gap, not a form
 * that quietly has three skills on it.
 *
 * The overall is taken from the bands that exist, so it appears as soon as
 * there is something to average and moves when a marked Writing paper arrives.
 * It is not an IELTS overall and the screen says so: a real one is the mean of
 * four skills, and one of those is not sat here.
 */
export function gatherResults(rows: ResultRow[]): TestResult[] {
  const groups = new Map<string, ResultRow[]>();
  for (const row of rows) {
    const key = row.test.setName?.trim() || row.test.title;
    groups.set(key, [...(groups.get(key) ?? []), row]);
  }

  const out: TestResult[] = [];
  for (const [name, papers] of groups) {
    const skills = Object.fromEntries(
      SKILLS.map((skill): [Skill, SkillBand] => {
        if (skill === 'SPEAKING') {
          return [skill, { band: null, state: 'not-offered', resultId: null }];
        }
        const paper = papers.find((row) => row.test.module === skill);
        if (!paper) return [skill, { band: null, state: 'not-sat', resultId: null }];
        if (paper.bandScore === null) {
          return [skill, { band: null, state: 'awaiting', resultId: paper.id }];
        }
        return [skill, { band: Number(paper.bandScore), state: 'marked', resultId: paper.id }];
      }),
    ) as Record<Skill, SkillBand>;

    const known = SKILLS.map((s) => skills[s].band).filter((b): b is number => b !== null);
    out.push({
      name,
      satAt: papers.map((p) => p.gradedAt).sort().at(-1) ?? '',
      skills,
      overall: meanBand(known),
      awaiting: SKILLS.some((s) => skills[s].state === 'awaiting'),
    });
  }

  return out.sort((a, b) => b.satAt.localeCompare(a.satAt));
}
